// kware start edit - Offline Operations: Results table
// REASON: Displays the outcome of each operation after batch execution.
// useMemo used to prevent MCLRenderer infinite re-render.

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { MultiColumnList, Badge, Icon, MessageBanner } from '@folio/stripes/components';
import { OPERATION_TYPES } from './ExcelParser';

const TYPE_LABELS = {
  [OPERATION_TYPES.CHECK_OUT]: 'ui-checkout.offline.type.checkOut',
  [OPERATION_TYPES.CHECK_IN]: 'ui-checkout.offline.type.checkIn',
  [OPERATION_TYPES.RENEW]: 'ui-checkout.offline.type.renew',
};

const TYPE_STYLES = {
  [OPERATION_TYPES.CHECK_OUT]: { background: '#1a5276', color: '#ffffff' },
  [OPERATION_TYPES.CHECK_IN]: { background: '#1e8449', color: '#ffffff' },
  [OPERATION_TYPES.RENEW]: { background: '#b7950b', color: '#ffffff' },
};

const VISIBLE_COLUMNS = ['displayId', 'type', 'patronBarcode', 'itemBarcode', 'actionDate', 'status', 'errorMessage'];

const COLUMN_WIDTHS = {
  displayId: '50px',
  type: '120px',
  patronBarcode: '150px',
  itemBarcode: '150px',
  actionDate: '170px',
  status: '120px',
};

const ResultsTable = ({ results }) => {
  const successCount = useMemo(() => results.filter(r => r.status === 'success').length, [results]);
  const errorCount = useMemo(() => results.filter(r => r.status === 'error').length, [results]);

  const numberedResults = useMemo(() => {
    const typeCounters = {};
    return results.map(r => {
      typeCounters[r.type] = (typeCounters[r.type] || 0) + 1;
      return { ...r, displayId: typeCounters[r.type] };
    });
  }, [results]);

  const columnMapping = useMemo(() => ({
    displayId: '#',
    type: <FormattedMessage id="ui-checkout.offline.column.type" />,
    patronBarcode: <FormattedMessage id="ui-checkout.offline.column.patronBarcode" />,
    itemBarcode: <FormattedMessage id="ui-checkout.offline.column.itemBarcode" />,
    actionDate: <FormattedMessage id="ui-checkout.offline.column.actionDate" />,
    status: <FormattedMessage id="ui-checkout.offline.column.status" />,
    errorMessage: <FormattedMessage id="ui-checkout.offline.column.errorMessage" />,
  }), []);

  const formatter = useMemo(() => ({
    displayId: (row) => row.displayId,
    type: (row) => (
      <span style={{
        ...TYPE_STYLES[row.type],
        padding: '3px 10px',
        borderRadius: '10px',
        fontSize: '12px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
      }}
      >
        <FormattedMessage id={TYPE_LABELS[row.type]} />
      </span>
    ),
    patronBarcode: (row) => row.patronBarcode || <Icon icon="minus" />,
    itemBarcode: (row) => row.itemBarcode,
    actionDate: (row) => (row.actionDate ? row.actionDate.replace('T', ' ').replace('.000Z', '') : ''),
    status: (row) => (
      row.status === 'success'
        ? <Badge color="primary"><FormattedMessage id="ui-checkout.offline.status.success" /></Badge>
        : <Badge color="red"><FormattedMessage id="ui-checkout.offline.status.error" /></Badge>
    ),
    errorMessage: (row) => row.errorMessage || '',
  }), []);

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
        <MessageBanner type="success">
          <FormattedMessage id="ui-checkout.offline.results.success" values={{ count: successCount }} />
        </MessageBanner>
        {errorCount > 0 && (
          <MessageBanner type="error">
            <FormattedMessage id="ui-checkout.offline.results.error" values={{ count: errorCount }} />
          </MessageBanner>
        )}
      </div>
      <MultiColumnList
        contentData={numberedResults}
        formatter={formatter}
        visibleColumns={VISIBLE_COLUMNS}
        columnMapping={columnMapping}
        columnWidths={COLUMN_WIDTHS}
      />
    </div>
  );
};

ResultsTable.propTypes = {
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default ResultsTable;
// kware end edit
